import { useEffect, useState } from "react";
import { Outlet, useLocation, useMatch } from "react-router-dom";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import { Header } from "../components/Header";
import { Sidebar } from "../components/Sidebar";
import { ErrorBoundary } from "../components/ErrorBoundary";
import { PhoneNavDrawer, PhoneTopBar } from "../components/phone/PhoneShell";
import { useIsPhone } from "../hooks/useIsPhone";

/**
 * Remember the last event the user was inside so the sidebar's event-scoped
 * links keep working when they navigate out to /users or /events. Without
 * this, stepping outside an event wipes the sidebar's event context and you
 * have to re-pick the event to get back in — jarring for organizers who
 * move between a specific event's pages and global admin screens.
 *
 * Derived from the current URL via useMatch (rather than a separate setter)
 * so it's always correct after deep-links, browser back/forward, and
 * programmatic navigation. `/events/new` matches the `/events/:eventId/*`
 * pattern too, so explicitly filter it out.
 *
 * Not persisted to localStorage — DashboardLayout stays mounted across all
 * authenticated navigation within a session, so in-memory state is enough.
 * A hard refresh legitimately resets context.
 */
function useActiveEventId(): string | null {
  const match = useMatch("/events/:eventId/*");
  const routeEventId =
    match?.params.eventId && match.params.eventId !== "new"
      ? match.params.eventId
      : null;

  const [lastEventId, setLastEventId] = useState<string | null>(null);

  useEffect(() => {
    if (routeEventId) setLastEventId(routeEventId);
  }, [routeEventId]);

  return routeEventId ?? lastEventId;
}

export function DashboardLayout() {
  const activeEventId = useActiveEventId();
  const location = useLocation();
  const isPhone = useIsPhone();
  const [phoneNavOpen, setPhoneNavOpen] = useState(false);

  // Only the bars around the page change below 900 px. The main element and
  // the Outlet keep their place in the tree, so the open page is never
  // remounted when the width crosses the breakpoint.
  return (
    <Box sx={{ display: "flex" }}>
      {isPhone ? (
        <PhoneTopBar onOpenNav={() => setPhoneNavOpen(true)} />
      ) : (
        <Header />
      )}
      {isPhone ? (
        <PhoneNavDrawer
          activeEventId={activeEventId}
          open={phoneNavOpen}
          onClose={() => setPhoneNavOpen(false)}
        />
      ) : (
        <Sidebar activeEventId={activeEventId} />
      )}
      <Box
        component="main"
        sx={isPhone ? { flexGrow: 1, p: 2, minWidth: 0 } : { flexGrow: 1, p: 3 }}
      >
        <Toolbar />
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </Box>
    </Box>
  );
}
